import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const toList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const fromList = (a) => (a || []).join(", ");

export function WorkAssignmentTab({ canManage, users = [] }) {
  return (
    <div className="space-y-8" data-testid="work-assignment-tab">
      <RoutingPreview />
      <TeamsSection canManage={canManage} users={users} />
      <RulesSection canManage={canManage} />
    </div>
  );
}

/* ----------------------------------------------------- live preview tester */
function RoutingPreview() {
  const [form, setForm] = useState({ service_type: "", engagement_type: "", country: "", language: "", citizenship: "" });
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/assignment/preview", form); setRec(data.recommendation); }
    catch (e) { toast.error("Preview failed"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="routing-preview">
      <div className="flex items-center gap-2 mb-1"><Icons.FlaskConical className="h-4 w-4 text-indigo-600" /><h3 className="font-heading font-medium text-slate-800">Routing Preview</h3></div>
      <p className="text-sm text-slate-500 mb-3">Test which team an engagement would be routed to. The engine evaluates Service → Jurisdiction → Country → Language → Citizenship.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <input className={inputCls} placeholder="Service type (e.g. U.S. Return)" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} data-testid="preview-service" />
        <input className={inputCls} placeholder="Engagement type" value={form.engagement_type} onChange={(e) => setForm({ ...form, engagement_type: e.target.value })} data-testid="preview-engagement-type" />
        <input className={inputCls} placeholder="Country of residence" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="preview-country" />
        <input className={inputCls} placeholder="Preferred language" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} data-testid="preview-language" />
        <input className={inputCls} placeholder="Citizenship" value={form.citizenship} onChange={(e) => setForm({ ...form, citizenship: e.target.value })} data-testid="preview-citizenship" />
        <button onClick={run} disabled={busy} data-testid="preview-run" className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-60">{busy && <Icons.Loader2 className="h-4 w-4 animate-spin" />}Evaluate</button>
      </div>
      {rec && (
        <div className="mt-3 bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-sm" data-testid="preview-result">
          <span className="text-slate-500">Recommended:</span> <b className="text-indigo-800">{rec.team_name}</b>
          <span className="text-slate-400"> ({rec.team_email || "no email"})</span>
          <div className="text-xs text-slate-500 mt-0.5">{rec.reason} {rec.rule_name ? `· rule: ${rec.rule_name}` : ""}</div>
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------- teams editor */
function TeamsSection({ canManage, users }) {
  const [teams, setTeams] = useState([]);
  const load = useCallback(() => api.get("/assignment/teams").then((r) => setTeams(r.data.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (key, patch) => {
    try { await api.patch(`/assignment/teams/${key}`, patch); toast.success("Team updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed"); }
  };

  return (
    <div data-testid="teams-section">
      <h3 className="font-heading font-medium text-slate-800 mb-3 flex items-center gap-2"><Icons.Users className="h-4 w-4 text-blue-600" />Teams</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {teams.map((t) => <TeamCard key={t.key} team={t} users={users} canManage={canManage} onSave={save} />)}
      </div>
    </div>
  );
}

function TeamCard({ team, users, canManage, onSave }) {
  const [email, setEmail] = useState(team.email || "");
  const [lead, setLead] = useState(team.team_lead_id || "");
  const dirty = email !== (team.email || "") || lead !== (team.team_lead_id || "");
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={`team-card-${team.key}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center"><Icons.Users className="h-4 w-4 text-blue-600" /></div>
          <div><div className="font-medium text-slate-800">{team.name}</div><div className="text-[11px] text-slate-400">{team.description}</div></div>
        </div>
        {team.active_flag === false && <span className="text-xs text-slate-400">Inactive</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
        <div><label className="text-[11px] text-slate-500">Team Email</label>
          <input className={inputCls} value={email} disabled={!canManage} onChange={(e) => setEmail(e.target.value)} data-testid={`team-email-${team.key}`} /></div>
        <div><label className="text-[11px] text-slate-500">Team Lead</label>
          <select className={inputCls} value={lead} disabled={!canManage} onChange={(e) => setLead(e.target.value)} data-testid={`team-lead-${team.key}`}>
            <option value="">None</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select></div>
      </div>
      {(team.jurisdiction_tags?.length > 0 || team.language_tags?.length > 0) && (
        <div className="flex flex-wrap gap-1 mt-2">
          {(team.jurisdiction_tags || []).map((j) => <span key={j} className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{j}</span>)}
          {(team.language_tags || []).map((l) => <span key={l} className="text-[10px] bg-violet-50 text-violet-600 rounded px-1.5 py-0.5">{l}</span>)}
        </div>
      )}
      {canManage && dirty && (
        <button onClick={() => onSave(team.key, { email, team_lead_id: lead || null })} data-testid={`team-save-${team.key}`} className="mt-3 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5 font-medium">Save</button>
      )}
    </div>
  );
}

/* ----------------------------------------------------- rules CRUD */
const BLANK_RULE = { name: "", order: 50, active: true, team_key: "us_tax", reason: "",
  conditions: { service_keywords: [], countries: [], languages: [], citizenships: [] } };

function RulesSection({ canManage }) {
  const [rules, setRules] = useState([]);
  const [teams, setTeams] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK_RULE);

  const load = useCallback(() => {
    api.get("/assignment/rules").then((r) => setRules(r.data.items)).catch(() => {});
    api.get("/assignment/teams").then((r) => setTeams(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing("new"); setForm(BLANK_RULE); };
  const startEdit = (r) => {
    setEditing(r.id);
    setForm({ name: r.name, order: r.order, active: r.active, team_key: r.team_key, reason: r.reason || "",
      conditions: { service_keywords: r.conditions?.service_keywords || [], countries: r.conditions?.countries || [], languages: r.conditions?.languages || [], citizenships: r.conditions?.citizenships || [] } });
  };
  const save = async () => {
    try {
      if (editing && editing !== "new") await api.patch(`/assignment/rules/${editing}`, form);
      else await api.post("/assignment/rules", form);
      toast.success("Rule saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
  };
  const del = async (id) => { try { await api.delete(`/assignment/rules/${id}`); toast.success("Rule deleted"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); } };
  const teamName = (k) => teams.find((t) => t.key === k)?.name || k;
  const routingTeams = teams.filter((t) => t.is_routing_team);
  const condSummary = (c) => {
    const parts = [];
    if (c?.service_keywords?.length) parts.push(`service: ${c.service_keywords.join("/")}`);
    if (c?.countries?.length) parts.push(`country: ${c.countries.length} listed`);
    if (c?.languages?.length) parts.push(`lang: ${c.languages.join("/")}`);
    if (c?.citizenships?.length) parts.push(`citizenship: ${c.citizenships.join("/")}`);
    return parts.length ? parts.join(" · ") : "Catch-all (matches everything)";
  };

  return (
    <div data-testid="rules-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-medium text-slate-800 flex items-center gap-2"><Icons.GitBranch className="h-4 w-4 text-indigo-600" />Assignment Rules</h3>
        {canManage && <button onClick={startNew} data-testid="rule-new-btn" className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 text-sm font-medium"><Icons.Plus className="h-4 w-4" />New Assignment Rule</button>}
      </div>
      <p className="text-sm text-slate-500 mb-3">Configure how new engagements and services are automatically assigned to the appropriate Team based on business rules. Rules are evaluated from top to bottom, and the first matching rule is applied unless multi-team assignment is enabled in a future release.</p>

      {editing && (
        <div className="bg-white border border-indigo-200 rounded-xl p-4 shadow-sm mb-3 space-y-3" data-testid="rule-editor">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <div className="sm:col-span-2"><label className="text-[11px] text-slate-500">Rule name</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rule-name" /></div>
            <div><label className="text-[11px] text-slate-500">Order</label><input type="number" className={inputCls} value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} data-testid="rule-order" /></div>
            <div><label className="text-[11px] text-slate-500">Assign to team</label>
              <select className={inputCls} value={form.team_key} onChange={(e) => setForm({ ...form, team_key: e.target.value })} data-testid="rule-team">
                {routingTeams.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {["service_keywords", "countries", "languages", "citizenships"].map((field) => (
              <div key={field}><label className="text-[11px] text-slate-500 capitalize">{field.replace("_", " ")} (comma-separated)</label>
                <input className={inputCls} value={fromList(form.conditions[field])} data-testid={`rule-${field}`}
                  onChange={(e) => setForm({ ...form, conditions: { ...form.conditions, [field]: toList(e.target.value) } })} /></div>
            ))}
          </div>
          <div><label className="text-[11px] text-slate-500">Reason (shown on assignment)</label><input className={inputCls} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="rule-reason" /></div>
          <div className="flex gap-2"><button onClick={save} disabled={!form.name.trim()} data-testid="rule-save" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50">Save Rule</button><button onClick={() => setEditing(null)} className="text-sm text-slate-500 px-3">Cancel</button></div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="rules-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <th className="px-4 py-2.5 font-semibold">Order</th><th className="px-4 py-2.5 font-semibold">Assignment Rule</th><th className="px-4 py-2.5 font-semibold hidden md:table-cell">Conditions</th><th className="px-4 py-2.5 font-semibold">→ Team</th><th className="px-4 py-2.5 font-semibold text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} className="border-b border-slate-50" data-testid={`rule-row-${r.id}`}>
                <td className="px-4 py-2.5 text-slate-500">{r.order}</td>
                <td className="px-4 py-2.5 text-slate-800 font-medium">{r.name}{r.is_default && <span className="ml-2 text-[10px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">default</span>}{!r.active && <span className="ml-2 text-[10px] bg-amber-50 text-amber-600 rounded px-1.5 py-0.5">inactive</span>}</td>
                <td className="px-4 py-2.5 text-slate-500 hidden md:table-cell text-xs">{condSummary(r.conditions)}</td>
                <td className="px-4 py-2.5"><span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">{teamName(r.team_key)}</span></td>
                <td className="px-4 py-2.5 text-right">
                  {canManage && (
                    <div className="inline-flex gap-1">
                      <button onClick={() => startEdit(r)} data-testid={`rule-edit-${r.id}`} className="p-1.5 hover:bg-slate-100 rounded text-slate-400"><Icons.Pencil className="h-3.5 w-3.5" /></button>
                      {!r.is_default && <button onClick={() => del(r.id)} data-testid={`rule-delete-${r.id}`} className="p-1.5 hover:bg-slate-100 rounded text-rose-400"><Icons.Trash2 className="h-3.5 w-3.5" /></button>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
